import React, { useEffect, useRef } from 'react';
import useVideoUploader from 'hooks/useVideoUploader';
import PropTypes from 'prop-types';
import useObjectUrl from 'hooks/useObjectUrl';

import SubmitBlocker from 'components/FinalForm/Fields/SubmitBlocker';
import PreviewVideo from './PreviewVideo';
import PreviewImage from './PreviewImage';
import Removable from './Removable';

const identify = value => {
  return !!value.video // This type is used for storing
    || value.type === 'video' // This type is used when server returns
    || (value instanceof File && value.type.match('video/.*')) // New upload
    ? 'video'
    : 'image';
};

const MediaAttachment = ({ fieldName, value, onUpdate, onRemove }) => {

  const { uploading, progress, upload, reset } = useVideoUploader();
  const isProcessed = useRef(false);

  // On unMount, make sure we're no longer uploading
  useEffect(() => {
    return () => {
      reset();
      isProcessed.current = false;
    };
  }, [reset]);

  // Transform files to upload format
  useEffect(() => {
    if (!(value instanceof File) || isProcessed.current) {
      return;
    }

    isProcessed.current = true;

    if (value.type.match('video/.*')) {
      upload(value)
        .then(({ cdnUrl, originalFilename }) => onUpdate({
          $id: value?.$id,
          video: cdnUrl,
          name: originalFilename,
          position: value.position,
        }))
        .catch(() => onRemove());
    } else {
      onUpdate({
        $id: value?.$id,
        image: value,
        position: value.position,
      });
    }
  }, [onRemove, onUpdate, upload, uploading, value]);

  const type = identify(value);

  const src = useObjectUrl(value instanceof File ? value : value.image) || value?.src;
  const srcSet = value?.srcSet;

  return (
    <Removable onClick={onRemove}>
      {type === 'video'
        ? <PreviewVideo uploading={uploading} progress={progress} name={value.name} poster={value.poster} />
        : <PreviewImage src={src} srcSet={srcSet} />}
      {type === 'video' && uploading && <SubmitBlocker name={`${fieldName}.uploading`}>Wait for upload to finish</SubmitBlocker>}
    </Removable>
  );
};

MediaAttachment.propTypes = {
  value: PropTypes.oneOfType([
    // New file, not yet uploaded nor formatted
    PropTypes.instanceOf(File),

    // (New) Formatted video ready for server
    PropTypes.shape({
      name: PropTypes.string,
      video: PropTypes.string.isRequired,
    }),

    // (New) Formatted image ready for server
    PropTypes.shape({
      image: PropTypes.instanceOf(File).isRequired,
    }),

    // File from server
    PropTypes.shape({
      id: PropTypes.any.isRequired,
      src: PropTypes.string,
      srcSet: PropTypes.string,
      poster: PropTypes.string,
      type: PropTypes.oneOf(['image', 'video']).isRequired,
    }),
  ]),

  fieldName: PropTypes.string.isRequired,

  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};


export default MediaAttachment;
